import { TopicSteps } from "./../../entities/topic/index";
import { ITopicUsers } from "../../entities/topic/index";
import { ITopicQuestion } from "../../entities/topic-question/index";
import { ITopic } from "../../entities/topic/index";
import { ITemplate } from "../../entities/template/index";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ITopicQuestionAnswer } from "../../entities/topic-question-answer";

interface TopicState {
  topic: ITopic | null;
  errors: string | null;
}

const initialState: TopicState = {
  topic: null,
  errors: null,
};

const topicSlice = createSlice({
  name: "topic",
  initialState,
  reducers: {
    userJoined: (
      state,
      action: PayloadAction<{ topicId: number; topicUser: ITopicUsers }>
    ) => {
      if (state.topic?.id === action.payload.topicId) {
        const existingUserIndex = state.topic.users.findIndex(
          (u) => u.id === action.payload.topicUser.id
        );

        if (existingUserIndex !== -1) {
          if (
            state.topic.users[existingUserIndex].status !==
            action.payload.topicUser.status
          ) {
            state.topic.users[existingUserIndex] = action.payload.topicUser;
          }
        } else {
          state.topic.users.push(action.payload.topicUser);
        }
      }
    },

    AddUsers: (state, action: PayloadAction<ITopicUsers[]>) => {
      if (state.topic) {
        state.topic.users = action.payload;
      }
    },

    topicStarted: (
      state,
      action: PayloadAction<{ topicId: number; topicQuestion: ITopicQuestion }>
    ) => {
      if (state.topic) {
        // state.topic.id = action.payload.topicId;
        // state.topic.currentQuestion = action.payload.topicQuestion;
        state.topic.step = TopicSteps.ANSWERING;
      }
    },
    questionTimerEnded: (
      state,
      action: PayloadAction<{ topicId: number; topicQuestionId: number }>
    ) => {
      if (state.topic) {
        state.topic.topicQuestionConfig.timer = 0;
        state.topic.step = TopicSteps.CREATOR_TO_CONTINUE;
      }
    },
    questionAnswered: (
      state,
      action: PayloadAction<{
        topicId: number;
        topicQuestionAnswer: ITopicQuestionAnswer;
        userId: number;
      }>
    ) => {
      if (state.topic) {
        state?.topic?.answers.push(action?.payload?.topicQuestionAnswer);
        if (
          action?.payload?.topicQuestionAnswer.userId ===
          action?.payload?.userId
        ) {
          state.topic.step = TopicSteps.CREATOR_TO_VALIDATE;
        }
      }
    },

    setAnswers: (state, action: PayloadAction<ITopicQuestionAnswer[]>) => {
      if (state.topic) {
        state.topic.answers = action.payload;
      }
    },
    questionAnswerValidated: (
      state,
      action: PayloadAction<{
        topicId: number;
        topicQuestionAnswer: {
          id: number;
          topicQuestionAnswerId: number;
          isCorrect: boolean;
        };
      }>
    ) => {
      if (state.topic?.currentQuestion?.topicId === action.payload.topicId) {
        const answer = state.topic.answers.find(
          (a) => a.id === action.payload.topicQuestionAnswer.id
        );

        if (answer) {
          answer.isCorrect = action.payload.topicQuestionAnswer.isCorrect;
        }
      }
    },

    answersSubmitted: (state, action: PayloadAction<{ topicId: number }>) => {
      if (state.topic?.id === action.payload.topicId) {
        state.topic.step = TopicSteps.CREATOR_TO_CONTINUE;
      }
    },
    questionAnswersSubmitted: (
      state,
      action: PayloadAction<{ topicId: number }>
    ) => {
      if (state.topic) {
        state.topic.step = TopicSteps.ANSWERING;
      }
    },

    errorOccurred: (state, action: PayloadAction<string>) => {
      state.errors = action.payload;
    },

    setTemplate: (state, action: PayloadAction<ITemplate>) => {
      if (state.topic) {
        state.topic.template = action.payload;
      }
    },

    changeTopicStep: (state, action: PayloadAction<TopicSteps>) => {
      if (state.topic) {
        state.topic.step = action.payload;
      }
    },
    topicCompleted: (state, action: PayloadAction<TopicSteps>) => {
      if (state.topic) {
        state.topic.step = TopicSteps.FINISHED;
      }
    },
    setCurrentQuestionFromApi: (
      state,
      action: PayloadAction<ITopicQuestion>
    ) => {
      if (state.topic) {
        state.topic.currentQuestion = action.payload;
      }
    },
    setCurrentQuestion: (state, action: PayloadAction<ITopicQuestion>) => {
      if (state.topic) {
        state.topic.currentQuestion = action.payload;
        state.topic.step = TopicSteps.ANSWERING;
        state.topic.topicQuestionConfig.completedQuestionsCount =
          state.topic.topicQuestionConfig.completedQuestionsCount + 1;
      }
    },
    userLeft: (
      state,
      action: PayloadAction<{ topicId: number; userId: number }>
    ) => {
      if (state.topic?.id === action.payload.topicId) {
        state.topic.users = state.topic.users.filter(
          (user) => user.id !== action.payload.userId
        );
      }
    },

    setTopic: (state, action: PayloadAction<ITopic | null>) => {
      if (action.payload) {
        state.topic = {
          ...action.payload,
          users: action.payload.users || [],
          answers: action.payload.answers || [],

          topicQuestionConfig: {
            id: action.payload.topicQuestionConfig.id,
            topicId: action.payload.topicQuestionConfig.topicId,
            timer: action.payload.topicQuestionConfig.timer,
            questionsCount: action.payload.topicQuestionConfig.questionsCount,
            completedQuestionsCount:
              action.payload.topicQuestionConfig.completedQuestionsCount || 0,
          },
        };
      }
    },
  },
});

export const {
  userJoined,
  userLeft,
  topicStarted,
  questionTimerEnded,
  questionAnswered,
  questionAnswerValidated,
  answersSubmitted,
  AddUsers,
  errorOccurred,
  setTemplate,
  setCurrentQuestion,
  setTopic,
  changeTopicStep,
  topicCompleted,
  setAnswers,
  setCurrentQuestionFromApi,
  questionAnswersSubmitted,
} = topicSlice.actions;

export default topicSlice.reducer;
